import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest, logout, onAccessTokenChange } from '@/lib/api';
import { ApiClientError } from '@/lib/api-error';
import { captureAccessTokenFromUrl } from '@/lib/auth-token';
import { meQueryKey } from '@/lib/query-keys';
import { getWebUrl } from '@/lib/urls';
import type { MeResponse } from '@/types/api';

type AuthContextValue = {
  user: MeResponse | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchMe(): Promise<MeResponse> {
  return apiRequest<MeResponse>('/me');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    captureAccessTokenFromUrl();
    setBootstrapped(true);
  }, []);

  useEffect(() => {
    return onAccessTokenChange((token) => {
      if (!token) {
        queryClient.setQueryData(meQueryKey, null);
      } else {
        void queryClient.invalidateQueries({ queryKey: meQueryKey });
      }
    });
  }, [queryClient]);

  const meQuery = useQuery({
    queryKey: meQueryKey,
    enabled: bootstrapped,
    retry: false,
    queryFn: async (): Promise<MeResponse | null> => {
      try {
        return await fetchMe();
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 401) {
          return null;
        }
        throw error;
      }
    },
  });

  const signOut = useCallback(async () => {
    await logout();
    queryClient.setQueryData(meQueryKey, null);
    await queryClient.invalidateQueries({ queryKey: meQueryKey });
    window.location.assign(getWebUrl());
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: meQuery.data ?? null,
      isLoading: !bootstrapped || meQuery.isLoading,
      isAuthenticated: Boolean(meQuery.data),
      error: meQuery.error ?? null,
      refetch: async () => {
        await meQuery.refetch();
      },
      signOut,
    }),
    [bootstrapped, meQuery, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
